import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { IntegrationPage, toMessage } from "../components/integration-shell";
import { ApiUnavailableState, EmptyState, LoadingState } from "../components/states";
import {
  SafeTestPanel,
  StepRail,
  ValidationFindings,
  RssPreview,
  SEND_TEST_LABEL,
  type FlowStep,
} from "../components/integrations";
import {
  api,
  type IntegrationConfigValidation,
  type RssItemView,
  type SafeIntegration,
  type SafeTestResult,
} from "../lib/api";

export interface RssConfigDraft {
  feedUrl: string;
  displayName: string;
  pollIntervalSeconds: number;
  targetGuildId: string;
  targetChannelKey: string;
  itemFilter: string;
  duplicatePolicy: "content-hash" | "external-id" | "url";
  maxItemsPerPoll: number;
  notificationTemplate: string;
  enabled: boolean;
}

function readDraft(config: unknown): RssConfigDraft {
  const c = (config ?? {}) as Record<string, unknown>;
  const str = (k: string) => (typeof c[k] === "string" ? (c[k] as string) : "");
  const num = (k: string, d: number) => (typeof c[k] === "number" ? (c[k] as number) : d);
  return {
    feedUrl: str("feedUrl"),
    displayName: str("displayName"),
    pollIntervalSeconds: num("pollIntervalSeconds", 900),
    targetGuildId: str("targetGuildId"),
    targetChannelKey: str("targetChannelKey"),
    itemFilter: str("itemFilter"),
    duplicatePolicy: (["content-hash", "external-id", "url"] as const).includes(c["duplicatePolicy"] as never)
      ? (c["duplicatePolicy"] as RssConfigDraft["duplicatePolicy"])
      : "content-hash",
    maxItemsPerPoll: num("maxItemsPerPoll", 5),
    notificationTemplate: str("notificationTemplate"),
    enabled: c["enabled"] === true,
  };
}

/**
 * The 10-step structured RSS setup flow (section 28). The browser NEVER fetches
 * the feed URL: every validation and preview goes through the control API (which
 * applies SSRF protection server-side). The explicit test action is SAFE — it
 * validates and previews only, it never delivers a live message.
 */
export function RssSetupFlow({
  integration,
  preview,
  validation,
  testResult,
  busy,
  message,
  onSaveValidate,
  onPreview,
  onTest,
  onEnable,
}: {
  integration: SafeIntegration;
  preview: RssItemView[] | null;
  validation: IntegrationConfigValidation | null;
  testResult: SafeTestResult | null;
  busy?: boolean;
  message?: string | null;
  onSaveValidate: (draft: RssConfigDraft) => void;
  onPreview: () => void;
  onTest: () => void;
  onEnable: (draft: RssConfigDraft) => void;
}): JSX.Element {
  const [draft, setDraft] = useState<RssConfigDraft>(() => readDraft(integration.configuration));
  const [ack, setAck] = useState(false);
  const set = <K extends keyof RssConfigDraft>(k: K, v: RssConfigDraft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const validated = validation?.valid === true;
  const steps: FlowStep[] = useMemo(
    () => [
      { id: "url", label: "1. Feed URL", done: draft.feedUrl.length > 0 },
      { id: "validate", label: "2. Validate (safe)", done: validated },
      { id: "preview", label: "3. Preview feed", done: preview !== null },
      { id: "target", label: "4. Guild + channel", done: draft.targetGuildId.length > 0 && draft.targetChannelKey.length > 0 },
      { id: "filter", label: "5. Item filter", done: draft.itemFilter.length > 0 },
      { id: "template", label: "6. Template", done: draft.notificationTemplate.length > 0 },
      { id: "interval", label: "7. Interval", done: draft.pollIntervalSeconds >= 60 },
      { id: "test", label: "8. Test (no send)", done: preview !== null && validated },
      { id: "send-test", label: "9. Send approved test", done: testResult !== null },
      { id: "enable", label: "10. Enable", done: integration.status !== "CONFIGURING" || draft.enabled },
    ],
    [draft, validated, preview, testResult, integration.status],
  );
  const activeId = steps.find((s) => !s.done)?.id ?? "enable";

  return (
    <div className="ob-builder">
      <div className="dryrun-banner" role="note">
        <strong>Safe by design.</strong>
        <span>The feed URL is validated and fetched only by the control API — never by your browser. No message is sent until you deliberately enable the feed.</span>
      </div>

      <StepRail steps={steps} activeId={activeId} />

      <div className="world-panel world-panel--proposed">
        <h4 className="section-title" style={{ marginTop: 0 }}>
          Feed configuration
        </h4>
        <div className="card-grid">
          <label className="field">
            <span className="field__label">Feed URL</span>
            <input className="field__input" value={draft.feedUrl} onChange={(e) => set("feedUrl", e.target.value)} aria-label="RSS feed URL" placeholder="https://example.com/feed.xml" />
          </label>
          <label className="field">
            <span className="field__label">Display name</span>
            <input className="field__input" value={draft.displayName} onChange={(e) => set("displayName", e.target.value)} aria-label="RSS display name" />
          </label>
          <label className="field">
            <span className="field__label">Target guild id</span>
            <input className="field__input" value={draft.targetGuildId} onChange={(e) => set("targetGuildId", e.target.value)} aria-label="RSS target guild id" />
          </label>
          <label className="field">
            <span className="field__label">Target channel key</span>
            <input className="field__input" value={draft.targetChannelKey} onChange={(e) => set("targetChannelKey", e.target.value)} aria-label="RSS target channel key" />
          </label>
          <label className="field">
            <span className="field__label">Item filter (optional)</span>
            <input className="field__input" value={draft.itemFilter} onChange={(e) => set("itemFilter", e.target.value)} aria-label="RSS item filter" />
          </label>
          <label className="field">
            <span className="field__label">Duplicate policy</span>
            <select className="field__input" value={draft.duplicatePolicy} onChange={(e) => set("duplicatePolicy", e.target.value as RssConfigDraft["duplicatePolicy"])} aria-label="RSS duplicate policy">
              <option value="content-hash">content-hash</option>
              <option value="external-id">external-id</option>
              <option value="url">url</option>
            </select>
          </label>
          <label className="field">
            <span className="field__label">Poll interval (seconds)</span>
            <input className="field__input" type="number" value={draft.pollIntervalSeconds} onChange={(e) => set("pollIntervalSeconds", Number(e.target.value))} aria-label="RSS poll interval seconds" />
          </label>
          <label className="field">
            <span className="field__label">Max items per poll</span>
            <input className="field__input" type="number" value={draft.maxItemsPerPoll} onChange={(e) => set("maxItemsPerPoll", Number(e.target.value))} aria-label="RSS max items per poll" />
          </label>
        </div>
        <label className="field">
          <span className="field__label">Notification template</span>
          <textarea className="field__input" rows={2} value={draft.notificationTemplate} onChange={(e) => set("notificationTemplate", e.target.value)} aria-label="RSS notification template" />
        </label>

        <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap", marginTop: "0.5rem" }}>
          <button type="button" className="btn btn--primary" disabled={busy || draft.feedUrl.length === 0} onClick={() => onSaveValidate(draft)}>
            {busy ? "Working…" : "Save & validate feed (safe)"}
          </button>
          <button type="button" className="btn btn--secondary" disabled={busy} onClick={onPreview}>
            Preview normalized feed
          </button>
        </div>
        {validation ? <ValidationFindings validation={validation} /> : null}
        {message ? <p className="muted" role="status">{message}</p> : null}
      </div>

      <div className="world-panel world-panel--current">
        <h4 className="section-title" style={{ marginTop: 0 }}>
          Feed health &amp; last items
        </h4>
        {preview === null ? <p className="muted">Preview the feed to see normalized items, the last item and duplicate handling.</p> : <RssPreview items={preview} />}
      </div>

      <SafeTestPanel result={testResult} onRunTest={onTest} busy={busy} />

      <div className="world-panel world-panel--proposed" style={{ marginTop: "0.75rem" }}>
        <h4 className="section-title" style={{ marginTop: 0 }}>
          Send approved test &amp; enable
        </h4>
        <label className="confirm-gate__ack" style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} aria-label="Acknowledge safe test" />
          <span>I understand this validated preview does not post to Discord until I enable the feed.</span>
        </label>
        <div className="row" style={{ gap: "0.5rem", marginTop: "0.5rem" }}>
          <button type="button" className="btn btn--secondary" disabled={busy || !ack || !validated} onClick={onTest}>
            {SEND_TEST_LABEL}
          </button>
          <button type="button" className="btn btn--primary" disabled={busy || !validated} onClick={() => onEnable({ ...draft, enabled: true })}>
            Enable feed
          </button>
        </div>
      </div>
    </div>
  );
}

export function IntegrationRss(): JSX.Element {
  return (
    <IntegrationPage title="RSS feed setup" intro={<p>Add and monitor an RSS feed in ten guided, safe steps.</p>}>
      {(tenantId) => <RssPanel tenantId={tenantId} />}
    </IntegrationPage>
  );
}

function RssPanel({ tenantId }: { tenantId: string }): JSX.Element {
  const [params] = useSearchParams();
  const [list, setList] = useState<SafeIntegration[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(params.get("id"));
  const [integration, setIntegration] = useState<SafeIntegration | null>(null);
  const [preview, setPreview] = useState<RssItemView[] | null>(null);
  const [validation, setValidation] = useState<IntegrationConfigValidation | null>(null);
  const [testResult, setTestResult] = useState<SafeTestResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.integrations
      .list(tenantId)
      .then((r) => {
        const rss = r.integrations.filter((i) => i.type === "RSS");
        setList(rss);
        if (!selectedId && rss[0]) setSelectedId(rss[0].id);
      })
      .catch((e) => setError(toMessage(e)));
  }, [tenantId]);

  useEffect(() => {
    if (!selectedId) return;
    api.integrations
      .get(tenantId, selectedId)
      .then((r) => {
        setIntegration(r.integration);
        setValidation(null);
        setPreview(null);
        setTestResult(null);
      })
      .catch((e) => setError(toMessage(e)));
  }, [tenantId, selectedId]);

  const saveValidate = async (draft: RssConfigDraft) => {
    if (!integration) return;
    setBusy(true);
    setMessage(null);
    try {
      await api.integrations.updateConfiguration(tenantId, integration.id, draft);
      const r = await api.integrations.validate(tenantId, integration.id);
      setValidation(r.validation);
      setIntegration(r.integration);
      setMessage(r.validation.valid ? "Configuration saved and validated by the control API." : `Validation found ${r.validation.errors.length} issue(s).`);
    } catch (e) {
      setMessage(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const doPreview = async () => {
    if (!integration) return;
    setBusy(true);
    try {
      const r = await api.integrations.getRss(tenantId, integration.id);
      setPreview(r.items);
    } catch (e) {
      setMessage(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const doTest = async () => {
    if (!integration) return;
    setBusy(true);
    try {
      const r = await api.integrations.test(tenantId, integration.id);
      setTestResult(r);
      setMessage(r.note);
    } catch (e) {
      setMessage(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const doEnable = async (draft: RssConfigDraft) => {
    if (!integration) return;
    setBusy(true);
    try {
      const r = await api.integrations.updateConfiguration(tenantId, integration.id, draft);
      setIntegration(r.integration);
      setMessage("Feed configuration marked enabled. Activation still runs through ODIN's controlled pipeline.");
    } catch (e) {
      setMessage(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <ApiUnavailableState detail={error} />;
  if (list === null) return <LoadingState label="Reading RSS integrations…" />;

  return (
    <div className="ob-builder">
      <label className="field">
        <span className="field__label">RSS integration</span>
        <select className="field__input" value={selectedId ?? ""} onChange={(e) => setSelectedId(e.target.value || null)} aria-label="Select RSS integration">
          <option value="">Select an RSS integration…</option>
          {list.map((i) => (
            <option key={i.id} value={i.id}>
              {i.name} ({i.status})
            </option>
          ))}
        </select>
      </label>

      {integration ? (
        <RssSetupFlow
          integration={integration}
          preview={preview}
          validation={validation}
          testResult={testResult}
          busy={busy}
          message={message}
          onSaveValidate={saveValidate}
          onPreview={doPreview}
          onTest={doTest}
          onEnable={doEnable}
        />
      ) : (
        <EmptyState title="No RSS integration selected">Create an RSS integration on the Integrations page, then configure it here.</EmptyState>
      )}
    </div>
  );
}
